import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CustomText, StatusType, TaskType } from "App.types";
import { HistoryEditor, withHistory } from "slate-history";
import { BaseEditor, BaseElement, createEditor, Editor, Transforms } from "slate";
import { Button, Space, Tag } from "antd";
import API from "Api";
import cx from "classnames";
import { withCustomLogic } from "App.helpers";
import { AudioOutlined, CloseOutlined, SoundFilled } from "@ant-design/icons";
import SentenceMaskedLeaf from "./SentenceLeaf";
import { usePressEnter } from "./Helpers/usePressEnter";
import { SentenceTaskProps } from "./SentenceTask.type";
import { useOnComplete } from "./Helpers/useOnComplete";
import { useAudioTranscript } from "./Helpers/useAudioTranscript";
import TaskPanel from "Components/TaskPanel";
import styles from "./SentenceTask.module.scss";
import useCompletedSlate from "./Helpers/useCompletedSlate";
import { useIsBeginner } from "Hooks/useIsBeginner";
import { ReactComponent as KeyboardSvg } from "Assets/keyboard.svg";
import { NotifyContext } from "App";
import usePrevious from "Hooks/usePrevious";
import Cookies from "js-cookie";
import AudioRecorder from "./FreePractice/AudioRecorder";

const initialValue = [{ children: [{ text: "" }] }];

export type CustomElement = { children: CustomText[] } & BaseElement;

declare module "slate" {
  export interface CustomTypes {
    Editor: ReactEditor & BaseEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const SentenceTask: FC<SentenceTaskProps> = ({
  sentence,
  sentence: { id, text, tags, translate, storyId, phrases, button },
  activeType,
  lesson,
  onTaskComplete,
  onNext,
  children,
  audio,
  showSuccessMessage,
  alignCenter = false,
  setTry,
  noTranslate,
  transcripts,
  showGrammar,
  setDictOpened,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [isLoading, setIsLoading] = useState(false);
  const [activeLeaf, setActiveLeaf] = useState<number | null>(null);
  const [hints, setHints] = useState<string[]>([]);
  const [isVoiceMode, setVoiceMode] = useState<boolean>(
    Cookies.get("voice_mode") ? Cookies.get("voice_mode") === "true" : activeType === TaskType.VoiceTranslate,
  );

  useEffect(() => {
    Cookies.set("voice_mode", `${isVoiceMode}`);
  }, [isVoiceMode]);

  const editor = useMemo(() => withReact(withHistory(withCustomLogic(createEditor()))), []);

  const setInitialState = useCallback(
    (withFocus: boolean = true) => {
      editor.children.forEach(() => {
        Transforms.delete(editor, { at: [0] });
      });

      editor.children = [];

      Transforms.insertNodes(editor, initialValue, { select: withFocus });
      if (withFocus) {
        try {
          ReactEditor.focus(editor);
        } catch (e) {}
      }
    },
    [editor],
  );

  const setCompletedState = useCompletedSlate({ tags, text, editor });

  // initial
  useEffect(() => {
    setActiveLeaf(null);
    setStatus(StatusType.Editing);
    if (!isVoiceMode) setInitialState(true);
    const hintTags = tags.filter((t, idx) => t.isHint && !phrases.some((ph) => ph.positionsIdx.includes(idx))).map((t) => t.lemma);
    const hintPhrases = phrases.filter((ph) => ph.positionsIdx.some((idx) => tags[idx].isHint)).map((ph) => ph.text);

    setHints([...hintTags, ...hintPhrases]);
  }, [sentence.id, setInitialState, setCompletedState, tags, phrases, isVoiceMode]);

  // audio transcript
  useAudioTranscript({ setActiveLeaf, sentence, audio, transcripts });

  const onComplete = useOnComplete({ text, tags, setStatus, editor, onTaskComplete, showSuccessMessage });

  const renderLeaf = useCallback(
    (props: any) => (
      <SentenceMaskedLeaf
        underlined={props.leaf.audioIdx === (activeLeaf ?? -1)}
        showErrors={!["editing", "completed", "loading", "isRecording", ""].includes(status)}
        {...props}
      />
    ),
    [status, activeLeaf],
  );

  const notifyApi = useContext(NotifyContext);

  const checkAudio = useCallback(
    async (audioData: Blob) => {
      setIsLoading(true);
      const file = new File([audioData], "record");

      const form = new FormData();
      form.append("file", file);
      form.append("id", `${sentence?.id}`);

      setIsLoading(true);

      const { comments, wrong, transcript } = await API.practice.speech(form).finally(() => setIsLoading(false));

      if (transcript) {
        Transforms.select(editor, {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        });

        Transforms.removeNodes(editor);
        Transforms.insertNodes(editor, [{ children: [{ text: transcript }] }]);
      }

      notifyApi.destroy();

      if (wrong) {
        notifyApi.error({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
        API.event.save({ sentence, text: transcript, type: "mistake", task: activeType, lesson: { id: lesson.id } });
      } else {
        notifyApi.success({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
        return onComplete(false);
      }
    },
    [activeType, editor, lesson.id, notifyApi, onComplete, sentence],
  );

  const onCheck = useCallback(async () => {
    const userAnswer = editor.children[0]?.children?.map((el: CustomText) => el.text).join("");
    if (!userAnswer) return notifyApi?.info({ message: "выполни задание!" });

    setIsLoading(true);
    setTry?.();

    notifyApi.destroy();

    // if (isEqualText(userAnswer, text)) {
    //   return onComplete?.();
    // }
    //
    // const altAnswer = alternatives.find((alt) => isEqualText(userAnswer, alt.text));
    // if (altAnswer) {
    //   notifyApi?.success({ message: "допустимый вариант перевода!" });
    //
    //   Transforms.select(editor, {
    //     anchor: Editor.start(editor, []),
    //     focus: Editor.end(editor, []),
    //   });
    //
    //   Transforms.removeNodes(editor);
    //   Transforms.insertNodes(editor, [{ children: [{ text: altAnswer.text }] }]);
    //   return onComplete?.(undefined);
    // }

    const { comments, wrong, answer } = await API.sentence
      .check(sentence.id, { answer: userAnswer, lessonId: lesson.id })
      .finally(() => setIsLoading(false));

    if (wrong) {
      notifyApi.error({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
      API.event.save({ sentence, text: userAnswer, type: "mistake", task: activeType, lesson: { id: lesson.id } });
    } else {
      if (answer) {
        Transforms.removeNodes(editor);
        Transforms.insertNodes(editor, [{ children: [{ text: answer }] }]);
      }
      comments && notifyApi.success({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
      return onComplete(false);
    }
  }, [activeType, editor, lesson.id, notifyApi, onComplete, sentence, setTry]);

  const onHintAnswer = async () => {
    const text = editor.children[0]?.children?.map((el: CustomText) => el.text).join("");
    API.event.save({ text, type: "hint", task: activeType, lesson: { id: lesson.id }, sentence });

    // const status = await onCheck(text, true);
    const wrongIdx = editor.children[0]?.children.findIndex((child: CustomText) => child.status === "wrong");
    if (wrongIdx >= 0) {
      Transforms.insertText(editor, "", { at: [0, wrongIdx] });
      // await onCheck("", true);
    }

    // if (status) {
    //   const hasError = editor.children[0]?.children.every((el: CustomText, idx: number) => {
    //     if (["missed", "lemma", "typo"].includes(el.status || "")) {
    //       setTry(true);
    //       Transforms.setNodes(editor, { status: "success" }, { at: [0, idx] });
    //       Transforms.insertText(editor, el.word || el.answer || "", { at: [0, idx] });
    //       setActiveLeaf(el.idx || null);
    //       delay(() => setActiveLeaf(null), 2000);
    //       return false;
    //     }
    //
    //     if (el.status === "order") {
    //       editor.children.forEach(() => {
    //         Transforms.delete(editor, { at: [0] });
    //       });
    //
    //       editor.children = [];
    //       Transforms.insertNodes(editor, [{ children: getCompletedSlate(tags, text) }]);
    //       return false;
    //     }
    //     return true;
    //   });
    //
    //   if (hasError) {
    //     setTry();
    //   }
    // }
  };

  usePressEnter({ isCompleted: status === StatusType.Completed, onNext, onCheck });

  const isBeginner = useIsBeginner();

  const onReset = () => {
    setInitialState(status === StatusType.Editing);
    ReactEditor.focus(editor);
  };

  const notify = useContext(NotifyContext);

  const prevMode = usePrevious(isVoiceMode);

  useEffect(() => {
    if (!isVoiceMode) setInitialState();
    if (prevMode !== isVoiceMode) notify.info({ message: isVoiceMode ? "Голосовой перевод" : "Письменный перевод" });
  }, [isVoiceMode, notify, prevMode, setInitialState]);

  return (
    <div className={styles.sentenceTask}>
      <div className={cx(styles.content, { [styles.content_hasChildren]: !!children })}>
        <div className={styles.children}>{children}</div>

        <div className={cx(styles.slate, { [styles.slate_alignCenter]: alignCenter })}>
          <div className={styles.slate_wrapper}>
            <form spellCheck="false">
              <Slate editor={editor} initialValue={initialValue}>
                <Editable
                  className={styles.textArea}
                  readOnly={status === "completed"}
                  onKeyDown={() => {
                    setStatus(StatusType.Editing);
                    setVoiceMode(false);
                  }}
                  renderLeaf={renderLeaf}
                  //renderElement={(props) => <StoryElement isActive={activeSent === props.element.id} play={play} {...props} />}
                />
              </Slate>
            </form>
            {status !== "completed" && (
              <Button
                size={"small"}
                type={"text"}
                className={styles.btn__clear}
                icon={<CloseOutlined style={{ fontSize: 12 }} />}
                onClick={onReset}
              />
            )}
          </div>
          <div className={styles.translate}>
            {(!noTranslate || status === StatusType.Completed) &&
              [TaskType.Translate, TaskType.Convert, TaskType.VoiceTranslate].includes(activeType) &&
              translate}
          </div>
        </div>

        <div className={styles.bottom}>
          {status !== StatusType.Completed && (
            <Space>
              {hints.map((hint) => (
                <Tag style={{ fontSize: 16 }} bordered={false} key={hint}>
                  {hint}
                </Tag>
              ))}
            </Space>
          )}

          <Button
            icon={<SoundFilled />}
            onClick={() => {
              audio?.play();
              if (status !== StatusType.Completed) {
                ReactEditor.focus(editor);
              }
            }}
            style={{ visibility: text && (status === "completed" || activeType === TaskType.Dictate) ? "visible" : "hidden" }}
          />
        </div>
      </div>

      <TaskPanel
        isLoading={isLoading}
        onCheck={onCheck}
        lessonId={lesson.id}
        task={activeType}
        sentId={id}
        storyId={storyId}
        isCompleted={status === StatusType.Completed}
        setDictOpened={setDictOpened}
        onNext={onNext}
        audio={audio}
        onHint={onHintAnswer}
        showGrammar={showGrammar}
        nextButton={button}
      >
        {isVoiceMode ? (
          <Space>
            <AudioRecorder isLoading={isLoading} setAudioData={checkAudio} />
            {/*<RecordButton isSpeaking={isSpeaking} isRecording={status === StatusType.IsRecording} onClick={onRecordClick} />*/}
            <Button type={"link"} icon={<KeyboardSvg width={40} height={38} />} onClick={() => setVoiceMode(false)} />
          </Space>
        ) : (
          <Space>
            <Button loading={isLoading} type={"primary"} shape={"round"} onClick={onCheck}>
              {isBeginner ? "проверить" : "check"}
            </Button>
            <Button icon={<AudioOutlined />} onClick={() => setVoiceMode(true)} />
          </Space>
        )}
      </TaskPanel>
    </div>
  );
};

export default SentenceTask;
